import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import './Video.css';
import { reportError, reportWarning } from 'shared/sentry';
import { IS_SAFARI } from 'shared/utils/config/browserUtils';
import {
  setAmbientAudioSession,
  setPlaybackAudioSession,
} from 'shared/utils/audioSession';

const VideoPlayer = ({
  src,
  poster = null,
  className = '',
  showControls = true,
  muted = true,
  loop = false,
  playsInline = true,
  autoPlay = false,
  controlsList = 'nodownload noplaybackrate noremoteplayback',
  disablePictureInPicture = true,
  disableRemotePlayback = true,
  preventContextMenu = true,
  onError,
  fallbackText = 'Video unavailable',
  preload = 'metadata',
  style,
  showDurationBadge = false,
  onPlayStarted,
  onPause,
  onSeekingChange,
  onDurationReady,
  pauseWhenOutOfView = false,
  onScrollPause,
  lazyLoad = true,
  /** Chat: custom paused overlay before first play (badges hidden via CSS while playing). */
  messagingChromeSync = false,
  playbackStarted = false,
  ...props
}) => {
  const [hasError, setHasError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStartedPlayback, setHasStartedPlayback] = useState(playbackStarted);
  const [isVideoPaused, setIsVideoPaused] = useState(true);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isResumingAfterSeek, setIsResumingAfterSeek] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const tapLockRef = useRef(false);
  const networkErrorRetryCountRef = useRef(0);
  const isSeekingRef = useRef(false);
  const wasPlayingBeforeSeekRef = useRef(false);
  const isResumingAfterSeekRef = useRef(false);
  /** Mirrors `isPlaying` so seek handlers see the latest value (avoids stale closure during scrub). */
  const isPlayingRef = useRef(false);
  const isBufferingRef = useRef(false);
  const wasPlayingBeforeVisibilityRef = useRef(false);
  const isFullscreenRef = useRef(false);
  const scrollPositionBeforeFullscreenRef = useRef(0);
  const onPlayStartedRef = useRef(onPlayStarted);
  const onPauseRef = useRef(onPause);
  const onSeekingChangeRef = useRef(onSeekingChange);
  /** Tracks whether parent callbacks were notified — avoids missed `play` events after listener rebind. */
  const parentNotifiedPlayingRef = useRef(false);

  const setBuffering = useCallback((next) => {
    isBufferingRef.current = next;
    setIsBuffering(next);
  }, []);

  useEffect(() => {
    onPlayStartedRef.current = onPlayStarted;
  }, [onPlayStarted]);

  useEffect(() => {
    onPauseRef.current = onPause;
  }, [onPause]);

  useEffect(() => {
    onSeekingChangeRef.current = onSeekingChange;
  }, [onSeekingChange]);

  const handleError = (e) => {
    const video = videoRef.current;
    // On iOS Safari, e.nativeEvent.error is often null; read directly from the element.
    const mediaError = video?.error ?? e?.nativeEvent?.error ?? null;
    const errorCode = mediaError?.code;

    // MEDIA_ERR_ABORTED (1): expected when React sets `src` and we (or the browser)
    // restart selection via load() — not a real playback failure.
    if (errorCode === 1) {
      return;
    }

    // MEDIA_ERR_NETWORK (2): transient network drop — retry up to 3 times before showing fallback.
    // Also retry unknown/empty errors (common on Safari during signed-URL races).
    if (errorCode === 2 || (!errorCode && !mediaError)) {
      networkErrorRetryCountRef.current += 1;
      if (networkErrorRetryCountRef.current <= 3) {
        const delay = networkErrorRetryCountRef.current * 1500;
        setTimeout(() => {
          const v = videoRef.current;
          if (!v) return;
          v.load();
        }, delay);
        return;
      }
    }

    const reportedError = mediaError
      ? new Error(`Video failed to load (MediaError code ${errorCode}: ${mediaError.message || ''})`)
      : new Error('Video failed to load');
    reportError(reportedError, 'VideoPlayer', { extra: { src, errorCode } });
    setHasError(true);
    setBuffering(false);

    onError?.(e);
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    onDurationReady?.(Math.floor(video.duration));
  };

  const handleWaiting = () => {
    const video = videoRef.current;
    if (!video || video.paused) return;
    // Ignore spurious waiting/stalled while we already have playable frames
    if (video.readyState >= 3) {
      setBuffering(false);
      return;
    }
    setBuffering(true);
    setTimeout(() => {
      if (video.readyState < 3 && !video.paused) video.play().catch(() => { });
    }, 800);
  };

  /** Clears a stuck spinner when the element is advancing again (Safari often skips `playing`). */
  const clearBufferingIfPlaying = useCallback(() => {
    if (!isBufferingRef.current) return;
    const video = videoRef.current;
    if (!video || video.paused || video.seeking) return;
    if (video.readyState >= 2) {
      setBuffering(false);
    }
  }, [setBuffering]);

  const revealNativeControls = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.paused || !showControls) return;

    video.controls = true;

    // Firefox keeps the control bar hidden after pause→play until pointer move unless nudged.
    if (typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent)) {
      requestAnimationFrame(() => {
        void video.offsetHeight;
        video.focus({ preventScroll: true });
      });
    }
  }, [showControls]);

  /** Safari/WebKit: force native transport visible when paused after first play (incl. remount). */
  const nudgeSafariPausedControls = useCallback(() => {
    const video = videoRef.current;
    if (!video || !showControls || !video.paused) return;

    video.controls = true;

    if (!IS_SAFARI) return;

    requestAnimationFrame(() => {
      void video.offsetHeight;
      video.focus({ preventScroll: true });
    });
  }, [showControls]);

  const syncAudioSessionForVideo = useCallback((video) => {
    if (!video) return;
    // Muted inline previews should mix with Spotify/Apple Music instead of
    // taking exclusive iOS audio focus. Unmute upgrades to playback.
    if (video.muted || video.volume === 0) setAmbientAudioSession();
    else setPlaybackAudioSession();
  }, []);

  const handlePlaying = () => {
    isResumingAfterSeekRef.current = false; // sync before state flush
    setBuffering(false);
    setIsResumingAfterSeek(false);
    setIsVideoPaused(false);
    revealNativeControls();
  };

  const handlePlay = () => {
    const currentVideo = videoRef.current;
    if (!currentVideo) return;

    // Pause other videos
    document.querySelectorAll('video').forEach((v) => {
      if (v !== currentVideo && !v.paused && !v.ended) v.pause();
    });

    syncAudioSessionForVideo(currentVideo);
    setIsResumingAfterSeek(false);
    setIsVideoPaused(false);
    revealNativeControls();
  };

  const handlePause = () => {
    // Guard: seeking in progress OR we are resuming after seek (ref is set synchronously
    // in handleSeeked so it's reliable even before React re-renders).
    if (isSeekingRef.current || isResumingAfterSeekRef.current) return;

    const video = videoRef.current;
    if (video?.paused && !video.seeking) {
      setBuffering(false);
      setIsVideoPaused(true);
      if (isPlayingRef.current) {
        nudgeSafariPausedControls();
      }
    }
  };

  const handleSeeking = () => {
    const video = videoRef.current;
    if (!video) return;
    isSeekingRef.current = true;
    setIsSeeking(true);
    setBuffering(true);
    onSeekingChangeRef.current?.(true);
    // `video.paused` is often already true when `seeking` fires; prefer app state + actual element.
    wasPlayingBeforeSeekRef.current = isPlayingRef.current || !video.paused;
  };

  const handleSeeked = () => {
    const video = videoRef.current;
    if (!video) return;
    const shouldResume = wasPlayingBeforeSeekRef.current;
    isSeekingRef.current = false;
    setIsSeeking(false);
    onSeekingChangeRef.current?.(false);
    wasPlayingBeforeSeekRef.current = false;

    if (shouldResume) {
      // Set the ref synchronously so any pause event that fires in the same tick
      // (before React re-renders) is correctly guarded in handlePause.
      isResumingAfterSeekRef.current = true;
      setIsResumingAfterSeek(true);
      if (video.paused) {
        video.play().catch(() => {
          isResumingAfterSeekRef.current = false;
          setIsResumingAfterSeek(false);
          setBuffering(false);
        });
      } else {
        // Already playing at the new position — data was cached, no buffering needed
        isResumingAfterSeekRef.current = false;
        setIsResumingAfterSeek(false);
        setBuffering(false);
      }
    } else {
      // Seeked while paused: clear spinner immediately if data is already available
      if (video.readyState >= 3) {
        setBuffering(false);
      }
      // If readyState < 3 while paused, `canplay` will fire and clear it
    }
  };

  const safePlay = useCallback(async () => {
    if (tapLockRef.current) return;
    tapLockRef.current = true;

    const video = videoRef.current;
    if (!video) {
      tapLockRef.current = false;
      return;
    }

    try {
      syncAudioSessionForVideo(video);
      if (video.paused || video.ended) await video.play();
    } catch (err) {
      reportWarning(err, 'VideoPlayer play interrupted');
    } finally {
      setTimeout(() => (tapLockRef.current = false), 300);
    }
  }, [syncAudioSessionForVideo]);

  const safePause = useCallback(() => {
    if (tapLockRef.current) return;
    tapLockRef.current = true;

    const video = videoRef.current;
    if (video && !video.paused) video.pause();

    setTimeout(() => (tapLockRef.current = false), 300);
  }, []);

  // Grid tiles without native controls: tap toggles play/pause.
  // Feed/chat tiles with a custom play overlay must NOT play on whole-tile tap.
  const handleWrapperClick = () => {
    const video = videoRef.current;
    if (!video || showControls) return;

    if (video.paused) safePlay();
    else safePause();
  };

  const handleContextMenu = (e) => preventContextMenu && e.preventDefault();

  const handlePlayButtonClick = (e) => {
    e.stopPropagation();
    safePlay();
  };

  useEffect(() => {
    setHasStartedPlayback(playbackStarted);
  }, [playbackStarted]);

  const prevSrcRef = useRef(null);

  useEffect(() => {
    setHasError(false);
    setIsPlaying(false);
    isPlayingRef.current = false;
    parentNotifiedPlayingRef.current = false;
    setHasStartedPlayback(playbackStarted);
    setIsVideoPaused(true);
    setIsSeeking(false);
    setIsResumingAfterSeek(false);
    setBuffering(false);
    isSeekingRef.current = false;
    isResumingAfterSeekRef.current = false;
    wasPlayingBeforeSeekRef.current = false;
    networkErrorRetryCountRef.current = 0;

    const video = videoRef.current;
    const prevSrc = prevSrcRef.current;
    prevSrcRef.current = src;

    if (!video) return;

    // First mount: React's `src` attribute already starts loading — calling load()
    // here aborts that request and can fire MEDIA_ERR_ABORTED → false "unavailable".
    // Subsequent src changes (e.g. refreshed signed URL): Safari still needs load().
    if (prevSrc != null && prevSrc !== src) {
      video.pause();
      video.load();
    }
  }, [src]);

  useEffect(() => {
    if (!playbackStarted || !showControls) return undefined;

    const video = videoRef.current;
    if (!video) return undefined;

    const applySafariPausedChrome = () => {
      if (video.paused) {
        nudgeSafariPausedControls();
      }
    };

    video.addEventListener('loadeddata', applySafariPausedChrome);
    video.addEventListener('canplay', applySafariPausedChrome);
    if (video.readyState >= 2) {
      applySafariPausedChrome();
    }

    return () => {
      video.removeEventListener('loadeddata', applySafariPausedChrome);
      video.removeEventListener('canplay', applySafariPausedChrome);
    };
  }, [playbackStarted, showControls, src, nudgeSafariPausedControls]);

  useEffect(() => {
    isResumingAfterSeekRef.current = isResumingAfterSeek;
  }, [isResumingAfterSeek]);

  // Keep playback UI + parent callbacks in sync with the real element (Chrome can miss play/pause events).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const syncPausedState = () => {
      if (isSeekingRef.current || video.seeking || isResumingAfterSeekRef.current) return;

      const isActive = !video.paused && !video.ended;
      setIsVideoPaused(video.paused);

      if (isActive) {
        setIsPlaying(true);
        isPlayingRef.current = true;
        if (!showDurationBadge && !messagingChromeSync) {
          setHasStartedPlayback(true);
        }
        if (!parentNotifiedPlayingRef.current) {
          parentNotifiedPlayingRef.current = true;
          setHasStartedPlayback(true);
          onPlayStartedRef.current?.();
        }
      } else if (video.paused) {
        setIsPlaying(false);
        isPlayingRef.current = false;
        if (parentNotifiedPlayingRef.current) {
          parentNotifiedPlayingRef.current = false;
          nudgeSafariPausedControls();
          onPauseRef.current?.();
        }
      }
    };

    video.addEventListener('play', syncPausedState);
    video.addEventListener('playing', syncPausedState);
    video.addEventListener('pause', syncPausedState);
    video.addEventListener('ended', syncPausedState);
    // Chrome native controls can skip play/playing when listeners rebind — timeupdate catches it.
    video.addEventListener('timeupdate', syncPausedState);
    syncPausedState();

    return () => {
      video.removeEventListener('play', syncPausedState);
      video.removeEventListener('playing', syncPausedState);
      video.removeEventListener('pause', syncPausedState);
      video.removeEventListener('ended', syncPausedState);
      video.removeEventListener('timeupdate', syncPausedState);
    };
  }, [messagingChromeSync, nudgeSafariPausedControls, showDurationBadge, src]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    if (!isVideoPaused && isPlaying) {
      revealNativeControls();
    }
  }, [isVideoPaused, isPlaying, revealNativeControls]);

  // Cross-browser inline playback
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playsInline) return;
    video.setAttribute('x5-playsinline', 'true');
    video.setAttribute('x5-video-player-type', 'h5');
    video.setAttribute('x5-video-player-fullscreen', 'true');
  }, [playsInline]);

  const resetUiAfterScrollPause = useCallback(() => {
    const video = videoRef.current;
    const wasPlaying = isPlayingRef.current;
    isSeekingRef.current = false;
    setIsSeeking(false);
    setIsResumingAfterSeek(false);
    isResumingAfterSeekRef.current = false;
    setIsPlaying(false);
    isPlayingRef.current = false;
    parentNotifiedPlayingRef.current = false;
    setHasStartedPlayback(false);
    setIsVideoPaused(true);
    setBuffering(false);
    if (video && (showDurationBadge || messagingChromeSync)) {
      video.controls = false;
    }
    if (wasPlaying) {
      onPauseRef.current?.();
    }
    onScrollPause?.();
  }, [messagingChromeSync, onScrollPause, showDurationBadge]);

  // Pause when out of view or when the chat message list scrolls
  useEffect(() => {
    if (!pauseWhenOutOfView || !containerRef.current) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = videoRef.current;
          if (!video) return;
          if (isFullscreenRef.current) return;
          const hasPlaybackStateToReset =
            playbackStarted || hasStartedPlayback || video.classList.contains('video-has-started');

          if (entry.intersectionRatio < 0.1 && hasPlaybackStateToReset && document.visibilityState === 'visible') {
            if (!video.paused) {
              video.pause();
            }
            resetUiAfterScrollPause();
          }
        });
      },
      { threshold: [0, 0.1, 0.25, 0.5, 1] }
    );

    observer.observe(containerRef.current);

    const scrollRoot = containerRef.current.closest('.chat_wrapper');
    let scrollRafId = null;

    const handleChatScroll = () => {
      if (scrollRafId != null) return;

      scrollRafId = window.requestAnimationFrame(() => {
        scrollRafId = null;
        const video = videoRef.current;
        if (!video || video.ended || isFullscreenRef.current) return;
        if (document.visibilityState !== 'visible') return;
        const hasPlaybackStateToReset =
          playbackStarted || hasStartedPlayback || video.classList.contains('video-has-started');
        if (!hasPlaybackStateToReset) return;

        if (!video.paused) {
          video.pause();
        }
        resetUiAfterScrollPause();
      });
    };

    if (scrollRoot) {
      scrollRoot.addEventListener('scroll', handleChatScroll, { passive: true });
    }

    return () => {
      observer.disconnect();
      if (scrollRoot) {
        scrollRoot.removeEventListener('scroll', handleChatScroll);
      }
      if (scrollRafId != null) {
        window.cancelAnimationFrame(scrollRafId);
      }
    };
  }, [pauseWhenOutOfView, resetUiAfterScrollPause, src, playbackStarted, hasStartedPlayback]);

  // Resume on tab visibility (only if it was playing before)
  useEffect(() => {
    const handleVisibility = () => {
      const video = videoRef.current;
      if (!video) return;

      if (document.visibilityState === 'hidden') {
        wasPlayingBeforeVisibilityRef.current = !video.paused && !video.ended;
        if (!video.paused) video.pause();
      } else if (
        document.visibilityState === 'visible' &&
        wasPlayingBeforeVisibilityRef.current
      ) {
        video.play().catch(() => { });
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Track fullscreen to avoid unwanted pauses and scroll jumps on mobile
  useEffect(() => {
    const handleFullscreenChange = () => {
      const video = videoRef.current;
      const container = containerRef.current;
      if (!video || !container) return;

      const doc = document;
      const fullscreenElement =
        doc.fullscreenElement ||
        doc.webkitFullscreenElement ||
        doc.mozFullScreenElement ||
        doc.msFullscreenElement;

      const isNowFullscreen =
        fullscreenElement === video ||
        fullscreenElement === container ||
        (video.webkitDisplayingFullscreen != null &&
          video.webkitDisplayingFullscreen);

      if (isNowFullscreen) {
        isFullscreenRef.current = true;
        scrollPositionBeforeFullscreenRef.current =
          window.scrollY || window.pageYOffset || 0;
      } else {
        if (isFullscreenRef.current) {
          window.scrollTo({
            top: scrollPositionBeforeFullscreenRef.current || 0,
            behavior: 'auto',
          });
        }
        isFullscreenRef.current = false;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Recover frozen playback periodically
  useEffect(() => {
    const interval = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      if (!video.paused && video.readyState < 3) video.play().catch(() => { });
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Safari repaint fix
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleTimeUpdate = () => { if (video.readyState >= 2) video.style.transform = 'scale(1)'; };
    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, []);

  // Keep audio session in sync when the user mutes/unmutes via native controls.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    const handleVolumeChange = () => syncAudioSessionForVideo(video);
    video.addEventListener('volumechange', handleVolumeChange);
    return () => video.removeEventListener('volumechange', handleVolumeChange);
  }, [src, syncAudioSessionForVideo]);

  if (!src) return null;

  const useCustomPausedOverlay = showDurationBadge || messagingChromeSync;
  const suppressPausedUiDuringSeek =
    isResumingAfterSeek ||
    (isSeeking && wasPlayingBeforeSeekRef.current);
  const uiIsVideoPaused = isVideoPaused && !suppressPausedUiDuringSeek;
  const uiIsPlaying = isPlaying || suppressPausedUiDuringSeek;
  // Feed/bundle: parent owns playbackStarted (resets on scroll). Also started while actively playing.
  const hasPlaybackStarted = useCustomPausedOverlay
    ? (playbackStarted || uiIsPlaying)
    : (hasStartedPlayback || playbackStarted);
  const pausedBeforeFirstPlay = uiIsVideoPaused && !hasPlaybackStarted;
  const showCustomPlayOverlay = useCustomPausedOverlay && pausedBeforeFirstPlay;
  const hideNativeControls = useCustomPausedOverlay && pausedBeforeFirstPlay;
  // Chrome/Firefox already draw a native buffering indicator once controls are active.
  // Safari: keep our spinner, but native WebKit loading-panel is suppressed in CSS.
  const nativeControlsActive = !IS_SAFARI && showControls && hasPlaybackStarted && !hideNativeControls;

  return (
    <div
      ref={containerRef}
      className="video-wrapper"
      style={{ position: 'relative', width: '100%', height: '100%', ...style }}
      onClick={
        // Only grids without a center play button may toggle from the tile.
        !showControls && !showCustomPlayOverlay ? handleWrapperClick : undefined
      }
    >
      {hasError ? (
        <div className="video-fallback" role="status">
          {fallbackText}
        </div>
      ) : (
        <video
          ref={videoRef}
          src={src}
          poster={poster || undefined}
          className={[
            'cross-browser-video',
            className,
            useCustomPausedOverlay && !hasPlaybackStarted && 'hide_video_controls',
            hideNativeControls && 'hide-controls',
            hasPlaybackStarted && 'video-has-started',
            uiIsPlaying && 'video-is-playing',
            suppressPausedUiDuringSeek && 'video-is-seeking-playback',
          ]
            .filter(Boolean)
            .join(' ')}
          preload={preload}
          decoding="async"
          playsInline={playsInline}
          controls={showControls && hasPlaybackStarted && !hideNativeControls}
          muted={muted}
          loop={loop}
          autoPlay={autoPlay}
          controlsList={controlsList}
          disablePictureInPicture={disablePictureInPicture}
          disableRemotePlayback={disableRemotePlayback}
          loading={lazyLoad ? 'lazy' : 'auto'}
          onError={handleError}
          onLoadedMetadata={handleLoadedMetadata}
          onWaiting={handleWaiting}
          onStalled={handleWaiting}
          onPlaying={handlePlaying}
          onCanPlay={() => { networkErrorRetryCountRef.current = 0; setBuffering(false); }}
          onCanPlayThrough={() => setBuffering(false)}
          onTimeUpdate={clearBufferingIfPlaying}
          onContextMenu={handleContextMenu}
          onPlay={handlePlay}
          onPause={handlePause}
          onSeeking={handleSeeking}
          onSeeked={handleSeeked}
          style={{ width: '100%', height: '100%', display: 'block', opacity: 1 }}
          {...props}
        >
          Your browser does not support the video tag.
        </video>
      )}

      {!hasError && showCustomPlayOverlay && (
        <div
          className="custom-play-button-overlay"
          onClick={handlePlayButtonClick}
          role="button"
          aria-label="Play video"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handlePlayButtonClick(e); }}
        />
      )}

      {!hasError && !pausedBeforeFirstPlay && isBuffering && !nativeControlsActive && (
        <div className="video-buffering-overlay" aria-hidden="true">
          <div className="video-buffering-spinner" />
        </div>
      )}
    </div>
  );
};

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  poster: PropTypes.string,
  className: PropTypes.string,
  showControls: PropTypes.bool,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  playsInline: PropTypes.bool,
  autoPlay: PropTypes.bool,
  controlsList: PropTypes.string,
  disablePictureInPicture: PropTypes.bool,
  disableRemotePlayback: PropTypes.bool,
  preventContextMenu: PropTypes.bool,
  onError: PropTypes.func,
  fallbackText: PropTypes.string,
  preload: PropTypes.string,
  style: PropTypes.object,
  showDurationBadge: PropTypes.bool,
  onPlayStarted: PropTypes.func,
  onPause: PropTypes.func,
  onSeekingChange: PropTypes.func,
  onDurationReady: PropTypes.func,
  pauseWhenOutOfView: PropTypes.bool,
  onScrollPause: PropTypes.func,
  lazyLoad: PropTypes.bool,
  messagingChromeSync: PropTypes.bool,
  playbackStarted: PropTypes.bool,
};

export default VideoPlayer;
